// Image Processing Utilities for AI Chat
// Handles image resizing, validation, and base64 conversion for chat attachments

/**
 * Supported image MIME types for chat attachments
 */
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif'
] as const;

export type SupportedImageType = typeof SUPPORTED_IMAGE_TYPES[number];

/**
 * Configuration for image processing
 */
export const IMAGE_CONFIG = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_DIMENSION: 1568, // Claude's recommended max dimension (≤1.15 megapixels)
  JPEG_QUALITY: 0.9, // Quality for JPEG compression
  SUPPORTED_TYPES: SUPPORTED_IMAGE_TYPES
} as const;

/**
 * Image dimensions interface
 */
export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Result of image processing
 */
export interface ProcessedImage {
  blob: Blob;
  dataUrl: string;
  dimensions: ImageDimensions;
  size: number;
  mimeType: SupportedImageType;
}

/**
 * Validates if a file is a supported image type
 */
export function validateImageFile(file: File): {
  valid: boolean;
  error?: string;
} {
  // Check file type
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type as SupportedImageType)) {
    return {
      valid: false,
      error: `Unsupported file type: ${file.type}. Supported types: JPEG, PNG, WebP, GIF`
    };
  }

  // Check file size
  if (file.size > IMAGE_CONFIG.MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const maxSizeMB = (IMAGE_CONFIG.MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
    return {
      valid: false,
      error: `File too large: ${sizeMB}MB. Maximum size: ${maxSizeMB}MB`
    };
  }

  return { valid: true };
}

/**
 * Load an image from a File object
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateResizedDimensions(
  width: number,
  height: number,
  maxDimension: number
): ImageDimensions {
  if (width <= maxDimension && height <= maxDimension) {
    // No resize needed
    return { width, height };
  }

  const aspectRatio = width / height;

  if (width > height) {
    // Landscape
    return {
      width: maxDimension,
      height: Math.round(maxDimension / aspectRatio)
    };
  } else {
    // Portrait or square
    return {
      width: Math.round(maxDimension * aspectRatio),
      height: maxDimension
    };
  }
}

/**
 * Resize an image to fit within maxDimension while maintaining aspect ratio
 */
export async function resizeImage(
  file: File,
  maxDimension: number = IMAGE_CONFIG.MAX_DIMENSION
): Promise<ProcessedImage> {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Load image
  const img = await loadImage(file);
  const originalDimensions = { width: img.width, height: img.height };

  // Calculate new dimensions
  const newDimensions = calculateResizedDimensions(
    img.width,
    img.height,
    maxDimension
  );

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = newDimensions.width;
  canvas.height = newDimensions.height;

  // Draw resized image
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, newDimensions.width, newDimensions.height);

  // Convert to blob
  const mimeType = file.type as SupportedImageType;
  const quality = mimeType === 'image/jpeg' ? IMAGE_CONFIG.JPEG_QUALITY : undefined;

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      mimeType,
      quality
    );
  });

  // Convert to data URL for preview
  const dataUrl = canvas.toDataURL(mimeType, quality);

  console.log(
    `📐 Image resized: ${originalDimensions.width}x${originalDimensions.height} → ${newDimensions.width}x${newDimensions.height}`
  );
  console.log(
    `📦 Size: ${(file.size / 1024).toFixed(1)}KB → ${(blob.size / 1024).toFixed(1)}KB`
  );

  return {
    blob,
    dataUrl,
    dimensions: newDimensions,
    size: blob.size,
    mimeType
  };
}

/**
 * Convert a Blob to base64 string (for Claude API)
 */
export async function convertToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Process an image file for upload: validate, resize, and prepare
 */
export async function processImageForUpload(
  file: File
): Promise<ProcessedImage> {
  try {
    const processed = await resizeImage(file);
    return processed;
  } catch (error) {
    console.error('Failed to process image:', error);
    throw error;
  }
}

/**
 * Handle pasted image from clipboard
 */
export function extractImageFromClipboard(
  event: ClipboardEvent
): File | null {
  const items = event.clipboardData?.items;
  if (!items) {
return null;
}

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) {
        // Give it a meaningful name
        const timestamp = Date.now();
        const extension = item.type.split('/')[1];
        const namedFile = new File(
          [file],
          `pasted-screenshot-${timestamp}.${extension}`,
          { type: item.type }
        );
        return namedFile;
      }
    }
  }

  return null;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Generate a unique filename for upload
 */
export function generateUploadFilename(originalName: string, userId: string): string {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop() || 'jpg';
  const sanitizedName = originalName
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .substring(0, 50);
  return `${userId}/${timestamp}-${sanitizedName}`;
}
